import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatSliderModule } from '@angular/material/slider';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isDefined, isNil } from '@trimble-gcs/common';
import { FeatureFlagService } from '@trimble-gcs/feature-flags';
import { TranslationService } from '@trimble-gcs/i18n';
import { SelectArgs, SelectMap } from '@trimble-gcs/i18n-types';
import { ModusSelectModule, ModusSwitchModule } from '@trimble-gcs/modus';
import { map } from 'rxjs';
import { FeatureFlagKey } from '../../feature-flags/feature-flag-key';
import { ScandataModel } from '../../scandata/scandata.models';
import { DEFAULT_SCAN_3D_STYLE, Scan3dStyle } from '../models/scan-3d-style';
import { Scan3dStyleRenderingMode } from '../models/scan-3d-style-rendering-mode';
import { Scan3dStyleUom } from '../models/scan-3d-style-uom';
import { Scan3dClassificationComponent } from './scan-3d-classification/scan-3d-classification.component';
import { Scan3dClearanceComponent } from './scan-3d-clearance/scan-3d-clearance.component';
import { Scan3dElevationComponent } from './scan-3d-elevation/scan-3d-elevation.component';
import { Scan3dGlobalClassificationComponent } from './scan-3d-global-classification/scan-3d-global-classification.component';
import { Scan3dRgbComponent } from './scan-3d-rgb/scan-3d-rgb.component';
import { scan3dStylingTranslations } from './scan-3d-styling.translations';

@UntilDestroy()
@Component({
  selector: 'sd-scan-3d-styling',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ModusSwitchModule,
    ModusSelectModule,
    Scan3dClassificationComponent,
    Scan3dGlobalClassificationComponent,
    Scan3dClearanceComponent,
    MatSliderModule,
    Scan3dElevationComponent,
    Scan3dRgbComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './scan-3d-styling.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Scan3dStylingComponent {
  private readonly translationService = inject(TranslationService);
  private readonly featureFlagService = inject(FeatureFlagService<FeatureFlagKey>);

  readonly elevationPicking = input<boolean>(false);
  readonly scandataModel = input<ScandataModel>();
  readonly scan3dStyle = input.required<Scan3dStyle>();
  readonly scan3dStyleUom = input.required<Scan3dStyleUom>();

  readonly styleChange = output<Scan3dStyle>();
  readonly elevationPickingChange = output<boolean>();

  readonly showExperimental = signal(false);

  readonly showRgbSettings = computed(
    () => this.scan3dStyle().renderingMode === Scan3dStyleRenderingMode.Rgb,
  );

  readonly showScanClassifications = computed(
    () =>
      this.scan3dStyle().renderingMode === Scan3dStyleRenderingMode.Classification &&
      isDefined(this.scandataModel()),
  );

  readonly showGlobalClassificationSettings = computed(
    () =>
      this.scan3dStyle().renderingMode === Scan3dStyleRenderingMode.Classification &&
      isNil(this.scandataModel()),
  );

  readonly showClearanceSettings = computed(
    () => this.scan3dStyle().renderingMode === Scan3dStyleRenderingMode.Clearance,
  );

  readonly showElevationSettings = computed(
    () => this.scan3dStyle().renderingMode === Scan3dStyleRenderingMode.Elevation,
  );

  readonly hasStationIconCullingFeature = toSignal(
    this.featureFlagService.hasFeature$('3d_style_stationIconCulling'),
    {
      initialValue: false,
    },
  );

  // Station icon culling is a scene-wide concern; only expose the control
  // in the global settings panel, not when editing a per-scan style, and
  // only when the culling feature flag is enabled.
  readonly showStationIconCullingDistanceSettings = computed(
    () => isNil(this.scandataModel()) && this.hasStationIconCullingFeature(),
  );

  readonly showMinStationIconsPerScanSettings = computed(
    () => isNil(this.scandataModel()) && this.hasStationIconCullingFeature(),
  );

  readonly hasClearanceFeature = toSignal(
    this.featureFlagService.hasFeature$('3d_style_clearance'),
    {
      initialValue: false,
    },
  );

  readonly hasElevationFeature = toSignal(
    this.featureFlagService.hasFeature$('3d_style_elevation'),
    {
      initialValue: false,
    },
  );

  readonly translations = scan3dStylingTranslations();

  readonly pointShapeValue = computed(() => {
    const pointShapeType = 'paraboloid'; // hard-coded for now, may be determined from data in future
    const selectArgs: SelectArgs = { select: pointShapeType };
    const selectMap: SelectMap = this.translations.pointShape.type;
    return this.translationService.translateSelect(selectArgs, selectMap);
  });

  readonly sizeAttenuationValue = computed(() => {
    const sizeAttenuationType = 'off'; // hard-coded for now, may be determined from data in future
    const selectArgs: SelectArgs = { select: sizeAttenuationType };
    const selectMap: SelectMap = this.translations.sizeAttenuation.type;
    return this.translationService.translateSelect(selectArgs, selectMap);
  });

  readonly formGroup = new FormGroup({
    renderingMode: new FormControl<Scan3dStyleRenderingMode>(Scan3dStyleRenderingMode.Rgb, {
      nonNullable: true,
    }),
    blendIntensity: new FormControl<boolean>(false, { nonNullable: true }),
    showEyeDomeLighting: new FormControl<boolean>(false, { nonNullable: true }),
    pointBudget: new FormControl<number>(10000000, { nonNullable: true }),
    pointDensity: new FormControl<number>(1, { nonNullable: true }),
    pointSize: new FormControl<number>(1, { nonNullable: true }),
    stationIconCullingDistance: new FormControl<number>(
      DEFAULT_SCAN_3D_STYLE.stationIconCullingDistance,
      { nonNullable: true },
    ),
    minStationIconsPerScan: new FormControl<number>(
      DEFAULT_SCAN_3D_STYLE.minStationIconsPerScan,
      { nonNullable: true },
    ),
  });

  readonly scan3dStyleRenderingMode = Scan3dStyleRenderingMode;

  constructor() {
    this.subscribeToScan3dStyle();
    this.subscribeToRenderingModeChange();
    this.subscribeToFormValueChanges();
  }

  showExperimentalClick(event: MouseEvent) {
    if (event.shiftKey && event.altKey) {
      this.showExperimental.set(!this.showExperimental());
    }
  }

  onStyleChange(styleChange: Partial<Scan3dStyle>) {
    const style = this.scan3dStyle();

    const mergedStyle: Scan3dStyle = {
      ...style,
      ...styleChange,
    };

    this.styleChange.emit(mergedStyle);
  }

  onElevationPickClick() {
    const elevationPicking = !this.elevationPicking();
    this.elevationPickingChange.emit(elevationPicking);
  }

  private subscribeToScan3dStyle() {
    toObservable(this.scan3dStyle)
      .pipe(untilDestroyed(this))
      .subscribe((style) => {
        this.formGroup.patchValue(
          {
            renderingMode: style.renderingMode,
            blendIntensity: style.blendIntensity,
            showEyeDomeLighting: style.showEyeDomeLighting,
            pointBudget: style.pointBudget,
            pointDensity: style.pointDensity,
            pointSize: style.pointSize,
            stationIconCullingDistance:
              style.stationIconCullingDistance ??
              DEFAULT_SCAN_3D_STYLE.stationIconCullingDistance,
            minStationIconsPerScan:
              style.minStationIconsPerScan ??
              DEFAULT_SCAN_3D_STYLE.minStationIconsPerScan,
          },
          { emitEvent: false },
        );
      });
  }

  private subscribeToRenderingModeChange() {
    this.formGroup.controls.renderingMode.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((renderingMode) => {
        this.elevationPickingChange.emit(renderingMode === Scan3dStyleRenderingMode.Elevation);
      });
  }

  private subscribeToFormValueChanges() {
    this.formGroup.valueChanges
      .pipe(
        map((changes) => {
          const mergedStyle: Scan3dStyle = {
            ...this.scan3dStyle(),
            ...changes,
          };

          return mergedStyle;
        }),
        untilDestroyed(this),
      )
      .subscribe((style) => this.styleChange.emit(style));
  }
}
